import React from 'react'
import { Trans } from '@lingui/macro'
import {
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  TagBeta
} from 'front-end/src/components'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import { useSendersQuery } from '../../account/AccountPage.codegen'
import { useCampaignDefaultSenderQuery } from '../ManageCampaignPage.codegen'
import { notificationEmailAddress } from 'shared/emails'

/**
 * Feature-flagged select for email "sender". Expects value of email_identity_id.
 */
export const SenderSelect = (props) => {
  const { featureFlags } = useFeatureFlags()

  return featureFlags?.multiple_senders ? <SenderSelectComp {...props} /> : null
}

const SenderSelectComp = ({ value, onChange }) => {
  // if we have cache-and-network anywhere, running this query will always fire network requests
  // this is probably because we have admin "namespace" and every query is in admin too
  // this looks like solution but not really
  // https://github.com/apollographql/apollo-client/issues/6760#issuecomment-668188727
  //
  // (it was problem for surveys because there is a {loading && <Spinner />} which would cause unmount
  // of what was there before...
  const { data } = useSendersQuery({ fetchPolicy: 'no-cache' })
  const senders = data?.organisation?.emailIdentities ?? []

  // types are wrong, react-select supports number values
  let options: any[] = senders
    .filter((s) => s.verified_at)
    .map((s) => ({
      label: `${s.name} (${s.email})`,
      value: s.id
    }))

  // disabled info-state just to show that default will be used
  if (!options.length) {
    options = [{ label: notificationEmailAddress, value: null }]
  }

  let selection =
    options.length === 1
      ? options[0]
      : // find value or select first (in case no sender was used in campaign yet)
        options.find((o) => o.value === value) ?? options[0]

  return (
    <FormControl id="sender">
      <FormLabel>
        <Trans>Email sender</Trans> <TagBeta />
      </FormLabel>
      <Select
        isDisabled={options.length <= 1}
        options={options}
        value={selection}
        onChange={(o) => onChange(o?.value)}
      />
      <FormHelperText color="gray.400">
        <Trans>Sender details can be updated in account settings</Trans>
      </FormHelperText>
    </FormControl>
  )
}

export const useDefaultSender = (campaign_id) => {
  const { featureFlags } = useFeatureFlags()

  const { data } = useCampaignDefaultSenderQuery({
    variables: { id: campaign_id },
    skip: !(campaign_id && featureFlags?.multiple_senders)
  })

  return data?.admin?.campaign?.defaultEmailIdentity?.id ?? null
}
